import React, { useState, useMemo } from "react";
import { Box, Modal, Typography, InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { SAMPLE_PALETTES, T } from "./constants";

// Self-contained mirror of the app's Color Library popup
// (SegmentBuilder/Shared/ColorPalette/ColorLibrary). It is intentionally NOT the
// real ColorPaletteModal component: that one is bound to segmentBuilder redux
// (selected segment / wheel id) and dispatches segment mutations on select, so
// dropping it into the gallery-theme context would mis-fire. This reuses the
// same VISUAL language (Saved | Recent tabs, LibraryItem cards with overlapping
// swatches, Cancel / Select footer) and returns the chosen palette to the caller.
// Full decouple-and-reuse of the shared modal is a tracked follow-up.

const PaletteCard = ({ palette, selected, onSelect }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    aria-label={`Palette ${palette.name}`}
    onClick={() => onSelect(palette)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(palette);
      }
    }}
    sx={{
      border: `1px solid ${selected ? T.primary : T.line}`,
      borderRadius: "8px",
      p: "12px",
      cursor: "pointer",
      minHeight: 75,
      outline: "none",
      "&:focus-visible": { boxShadow: `0 0 0 2px ${T.primary}` },
      "&:hover": { borderColor: T.primary },
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, fontFamily: T.fontBody, mb: "10px" }}>
      {palette.name}
    </Typography>
    <Box sx={{ display: "flex" }}>
      {palette.colors.map((c, i) => (
        <Box
          key={i}
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: c,
            border: "1px solid #98a2b3",
            ml: i === 0 ? 0 : "-8px",
            zIndex: palette.colors.length - i,
          }}
        />
      ))}
    </Box>
  </Box>
);

const GalleryPaletteLibrary = ({ open, onClose, onSelect, palettes = SAMPLE_PALETTES }) => {
  const [tab, setTab] = useState("saved");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const recent = useMemo(() => palettes.slice(0, 3), [palettes]);
  const list = tab === "saved" ? palettes : recent;
  const filtered = useMemo(
    () => list.filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase())),
    [list, search]
  );

  const confirm = () => {
    if (selected) onSelect(selected);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="gt-color-library-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: "min(760px, 92vw)",
          maxHeight: "86vh",
          bgcolor: "#fff",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px", p: "20px 24px", borderBottom: `1px solid ${T.line}` }}>
          <Box
            role="button"
            aria-label="Back"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClose()}
            sx={{ cursor: "pointer", pt: "4px", "&:focus-visible": { outline: `2px solid ${T.primary}` } }}
          >
            <ArrowBackIcon sx={{ color: T.ink }} />
          </Box>
          <MenuBookRoundedIcon sx={{ color: T.ink, mt: "2px" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="gt-color-library-title" sx={{ fontSize: 22, fontWeight: 700, color: T.ink, fontFamily: T.fontBody }}>
              Color Library
            </Typography>
            <Typography sx={{ fontSize: 13, color: T.muted, fontFamily: T.fontBody }}>
              Store and organize color palettes for future use!
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", border: `1px solid ${T.line}`, borderRadius: "10px", px: "12px", py: "8px", minWidth: 200 }}>
            <SearchIcon sx={{ color: "#98a2b3", fontSize: 20 }} />
            <InputBase
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ "aria-label": "Search palettes" }}
              sx={{ fontSize: 14, fontFamily: T.fontBody, flex: 1 }}
            />
          </Box>
        </Box>

        {/* tabs */}
        <Box sx={{ display: "flex", gap: "24px", px: "24px", borderBottom: `1px solid ${T.line}` }}>
          {[
            { id: "saved", label: "Saved" },
            { id: "recent", label: "Recent" },
          ].map((tb) => (
            <Box
              key={tb.id}
              role="tab"
              aria-selected={tab === tb.id}
              tabIndex={0}
              onClick={() => setTab(tb.id)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setTab(tb.id)}
              sx={{
                py: "12px",
                cursor: "pointer",
                fontSize: 15,
                fontWeight: 600,
                fontFamily: T.fontBody,
                color: tab === tb.id ? "#81459B" : T.muted,
                borderBottom: tab === tb.id ? "2px solid #C283DA" : "2px solid transparent",
                "&:hover": { color: "#81459B" },
                "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
              }}
            >
              {tb.label}
            </Box>
          ))}
        </Box>

        {/* grid / empty */}
        <Box sx={{ flex: 1, overflow: "auto", p: "20px 24px" }}>
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: "40px" }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.ink, fontFamily: T.fontBody }}>
                No palettes found
              </Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, fontFamily: T.fontBody, mt: "4px" }}>
                {search ? "Try a different search." : "Saved palettes from the Wheel Builder appear here."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
              {filtered.map((p) => (
                <PaletteCard
                  key={p.id}
                  palette={p}
                  selected={selected?.id === p.id}
                  onSelect={setSelected}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* footer */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", p: "16px 24px", borderTop: `1px solid ${T.line}` }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClose()}
            sx={{ border: `1.5px solid ${T.primary}`, color: T.primary, borderRadius: "8px", px: "22px", py: "8px", fontSize: 14, fontWeight: 600, fontFamily: T.fontBody, cursor: "pointer", "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 } }}
          >
            Cancel
          </Box>
          <Box
            role="button"
            tabIndex={0}
            aria-disabled={!selected}
            onClick={confirm}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && confirm()}
            sx={{
              background: selected ? T.primary : "#CBB6F5",
              color: "#fff",
              borderRadius: "8px",
              px: "26px",
              py: "8px",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: T.fontBody,
              cursor: selected ? "pointer" : "not-allowed",
              "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
            }}
          >
            Select
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GalleryPaletteLibrary;
